<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { CircleSlash, RefreshCw, TriangleAlert } from 'lucide-vue-next'
import { useToaster } from '../composables/useToaster'
import {
  DEFAULT_APP_VERSION_POLL_INTERVAL_MS,
  DEFAULT_APP_VERSION_STALE_AFTER_MS,
  useAppVersionMonitor,
  type AppVersionDecision,
  type AppVersionInfo,
} from './appVersion'

export interface AppVersionRefreshGateProps {
  loadedVersion: AppVersionInfo
  versionUrl?: string
  pollIntervalMs?: number
  staleAfterMs?: number
}

const props = withDefaults(defineProps<AppVersionRefreshGateProps>(), {
  versionUrl: '/app-version.json',
  pollIntervalMs: DEFAULT_APP_VERSION_POLL_INTERVAL_MS,
  staleAfterMs: DEFAULT_APP_VERSION_STALE_AFTER_MS,
})

const toaster = useToaster()
const monitor = useAppVersionMonitor({
  loadedVersion: props.loadedVersion,
  versionUrl: props.versionUrl,
  pollIntervalMs: props.pollIntervalMs,
  staleAfterMs: props.staleAfterMs,
})

const activeErrorToastId = ref<string | undefined>()
const activeGentleToastId = ref<string | undefined>()
const shownGentleKeys = ref(new Set<string>())

const decision = computed(() => monitor.decision.value)
const showUnsupported = computed(() => decision.value.severity === 'unsupported')

watch(
  decision,
  (next) => {
    if (next.severity === 'unsupported') {
      dismissVersionToasts()
      return
    }

    if (next.severity === 'error') {
      dismissGentleToast()
      showErrorToast(next)
      return
    }

    dismissErrorToast()

    if (next.severity === 'gentle' && shouldShowGentleToast(next)) {
      showGentleToast(next)
      return
    }

    if (next.severity === 'none') {
      dismissGentleToast()
    }
  },
  { immediate: true },
)

function showGentleToast(next: AppVersionDecision) {
  dismissGentleToast()

  activeGentleToastId.value = toaster.create({
    title: 'A newer version is available',
    description: buildDescription(next),
    type: 'info',
    duration: Number.POSITIVE_INFINITY,
    action: {
      label: 'Refresh',
      onClick: refreshPage,
    },
  })

  shownGentleKeys.value.add(gentleDismissedStorageKey(next))
}

function showErrorToast(next: AppVersionDecision) {
  if (activeErrorToastId.value) {
    toaster.update(activeErrorToastId.value, {
      title: 'Refresh required',
      description: buildDescription(next),
      type: 'error',
      duration: Number.POSITIVE_INFINITY,
      action: {
        label: 'Refresh',
        onClick: refreshPage,
      },
    })
    return
  }

  activeErrorToastId.value = toaster.create({
    title: 'Refresh required',
    description: buildDescription(next),
    type: 'error',
    duration: Number.POSITIVE_INFINITY,
    action: {
      label: 'Refresh',
      onClick: refreshPage,
    },
  })
}

function dismissVersionToasts() {
  dismissGentleToast()
  dismissErrorToast()
}

function dismissGentleToast() {
  if (!activeGentleToastId.value) {
    return
  }

  toaster.dismiss(activeGentleToastId.value)
  activeGentleToastId.value = undefined
}

function dismissErrorToast() {
  if (!activeErrorToastId.value) {
    return
  }

  toaster.dismiss(activeErrorToastId.value)
  activeErrorToastId.value = undefined
}

function shouldShowGentleToast(next: AppVersionDecision) {
  return !shownGentleKeys.value.has(gentleDismissedStorageKey(next))
}

function buildDescription(next: AppVersionDecision) {
  return next.reason === 'week-old'
    ? 'A newer dashboard is available. Refresh when you have a moment.'
    : 'A newer dashboard is available. Refresh to get the latest updates.'
}

function refreshPage() {
  if (typeof window === 'undefined') {
    return
  }

  window.location.reload()
}

function gentleDismissedStorageKey(next: AppVersionDecision) {
  return [
    'se',
    props.loadedVersion.app,
    props.loadedVersion.channel,
    props.loadedVersion.version,
    'version-gentle-dismissed-at',
    next.reason,
    next.latest?.version ?? 'unknown',
    next.latest?.releasedAt ?? 'unknown',
  ].join(':')
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="showUnsupported"
      class="fixed inset-0 z-[2147483647] flex min-h-screen items-center justify-center bg-se-dark px-4 py-8 font-archivo text-se-white"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="app-version-refresh-title"
    >
      <div class="w-full max-w-md rounded-lg border border-se-danger bg-se-dark p-6 shadow-2xl">
        <div class="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-se-danger/15 text-se-danger">
          <CircleSlash class="h-5 w-5" />
        </div>
        <h1 id="app-version-refresh-title" class="text-2xl font-semibold">
          Please refresh Shopexperts
        </h1>
        <p class="mt-3 text-sm leading-6 text-se-gray-200">
          This version is no longer supported. Refresh the page to continue with version
          {{ decision.latest?.version ?? 'the latest release' }}.
        </p>
        <button
          type="button"
          class="mt-6 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-se-white px-4 py-2 text-sm font-semibold text-se-dark transition hover:bg-se-gray-100"
          @click="refreshPage"
        >
          <RefreshCw class="h-4 w-4" />
          Refresh now
        </button>
        <div class="mt-4 flex items-start gap-2 text-xs leading-5 text-se-gray-300">
          <TriangleAlert class="mt-0.5 h-4 w-4 shrink-0 text-se-danger" />
          <span>Loaded version {{ loadedVersion.version }} cannot safely keep running.</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>
